<template>
  <div class="container">
    <oie-product-filter-bar
      :use-compare="false"
      :current-count="products.length"
      :show-sort-order="false"
      :total-count="totalCount"
      :show-facet-filter-toggle="enableFilters"></oie-product-filter-bar>

    <div v-if="totalCount === 0 && searchQuery" class="product-list__no-result">
      <p class="product-list__no-result-title">
        {{ tStatic('noListResultTitle') }}
      </p>
      <p class="product-list__no-result-text">
        {{ tStatic('noListResultText') }}
      </p>

      <div class="product-list__no-result-contact">
        {{ tStatic('searchAgainDescription') }}
      </div>
    </div>

    <div v-else :class="['product-list__products-filter-wrapper', 'section-block--create-space-top']">
      <slot name="filters"></slot>

      <oie-section-block-shell :css-classes="['section-block--create-space', 'section-block--no-padding']">
        <template v-if="useSplitList">
          <h2>{{ t('vehicleRelatedProductsTitle') }}</h2>
          <p>{{ t('vehicleRelatedProductsText') }}</p>
          <oie-product-list-search
            v-if="loading || vehicleRelatedProducts.length > 0"
            :products="vehicleRelatedProducts"
            :use-list-view="useListViewComputed"
            :loading="loading"
            :type-of-listing="typeOfListing"></oie-product-list-search>
          <div v-if="!loading && vehicleRelatedProducts.length === 0" class="product-list__no-result">
            <p class="product-list__no-result-text">
              {{ t('vehicleRelatedProductsEmptyText') }}
            </p>
          </div>
          <h2>{{ t('universalProductsTitle') }}</h2>
          <p>{{ t('universalProductsText') }}</p>
          <oie-product-list-search
            v-if="loading || universalProducts.length > 0"
            :products="universalProducts"
            :use-list-view="useListViewComputed"
            :loading="loading"
            :type-of-listing="typeOfListing"></oie-product-list-search>

          <div v-if="!loading && universalProducts.length === 0" class="product-list__no-result">
            <p class="product-list__no-result-text">
              {{ t('universalProductsEmptyText') }}
            </p>
          </div>
        </template>

        <template v-else>
          <oie-product-list-search
            :products="products"
            :use-list-view="useListViewComputed"
            :loading="loading"
            :type-of-listing="typeOfListing"></oie-product-list-search>
        </template>
      </oie-section-block-shell>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import httpService from '@/core/http-service'
import _ from 'lodash'
import priceService from '@/core/price-service'
import utils from '@/core/utils'
import { selectedFilters, allFilterParameters, selectedVehicle } from '@/store/filters'

export default {
  props: {
    productIds: {
      type: Array,
      default: null
    },
    typeOfListing: {
      type: String,
      default: ''
    },
    enableFilters: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    technicalInformation: [],
    filters: [],
    productExpanded: null,
    products: [],
    page: 0,
    totalCount: 0,
    count: 0,
    madeReset: false,
    searchQuery: '',
    loading: false,
    showFilter: true,
    pageSize: 40
  }),
  computed: {
    ...mapGetters('ui', ['useListView', 'forceListView', 'forceGridView', 'showTechnicalnformation', 'useVat', 'isTechnicalInformationBlockActive']),
    ...mapGetters('productVariants', ['isExpanded', 'displayAllVariants']),
    useListViewComputed() {
      if (this.forceListView) { return true }
      if (this.forceGridView) { return false }
      return this.useListView
    },
    selectedFilters() { return selectedFilters() },
    allFilterParameters() { return allFilterParameters() },
    useSplitList() {
      return selectedVehicle()
    },
    vehicleRelatedProducts() {
      return this.useSplitList ? _.filter(this.products, p => !p.isUniversal) : []
    },
    universalProducts() {
      return this.useSplitList ? _.filter(this.products, p => p.isUniversal) : []
    }
  },
  watch: {
    products(value) {
      this.publish('product-list-page-wrapper.products-updated', value)
    },
    useVat: {
      immediate: true,
      handler() {
        const productIds = _.flatMap(this.products, 'id')
        if (productIds && productIds.length) {
          httpService.post('productListPrices', { productId: productIds }).then((response) => {
            priceService.populatePricesFromBackend(response.data, this.products)
          })
        }
      }
    },
    'clientModel.useListPrice': {
      immediate: true,
      handler() {
        const productIds = _.flatMap(this.products, 'id')
        if (productIds && productIds.length) {
          httpService.post('productListPrices', { productId: productIds }).then((response) => {
            priceService.populatePricesFromBackend(response.data, this.products)
          })
        }
      }
    },
    productIds() {
      this.loading = true
      this.page = 0
      this.products = []
      this.loadMore()
    }
  },
  async mounted() {
    this.page = (parseInt(utils.getQueryStringParam('p')) - 1) || 0

    this.searchQuery = utils.getQueryStringParam('query') || ''

    await this.loadMore()

    this.subscribe('dropdown-option-selected-sort-products', selectedOption => {
      this.products = []
      this.page = 0
      this.sortBy = selectedOption.value
    })

    this.subscribe('toggle.articles', (id) => {
      this.productExpanded = id
    })
  },
  methods: {
    ...mapActions('productVariants', ['openAllVariants', 'toggleShowAllVariants']),
    getLoadMoreBody(filters) {
      const defaultBody = {
        page: this.page,
        pageSize: this.getProductListPageSize(this.pageSize),
        query: this.searchQuery,
        ...filters
      }

      _.merge(defaultBody, { vehicleFilter: utils.getQueryStringParam('vehicleFilter'), categoryFilter: utils.getQueryStringParam('categoryFilter') })

      defaultBody.productIds = this.productIds

      return defaultBody
    },
    setProductListPrices(productIds) {
      httpService.post('productListPrices', { productId: productIds }).then((response) => {
        priceService.populatePricesFromBackend(response.data, this.products)
      })
    },
    async loadMore(filters = {}) {
      try {
        this.page++
        const body = this.getLoadMoreBody(filters)
        this.loading = true
        const response = await httpService.post('products', body)
        this.totalCount = response.data.totalCount
        this.products = this.products.concat(response.data.data)
        this.count = this.products.length
        const productIds = _.flatMap(response.data.data, 'id')
        this.setProductListPrices(productIds)
        this.loading = false

        const allProductsWithVariants = _.chain(this.products).filter(product => product.items.length > 1).map(p => p.id).value()
        this.openAllVariants(allProductsWithVariants)
      } catch(e) {
        // do nothing
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss">
    .product-list__no-result-contact {
        margin-top: 0.5rem;
    }
</style>
